import { PCA } from 'ml-pca';
import { Entity, Query, Dimension } from '../api/OasisBackendApi';

export interface PCAFitResult {
    pcaModel: any;
    standardizationParams: {
        means: number[];
        stds: number[];
    };
    dimensionVectors: Array<{ dimension: Dimension; x: number; y: number; magnitude: number }>;
    explainedVariance: { pc1: number; pc2: number; total: number };
}

export interface PCAResult extends PCAFitResult {
    transformedPoints: Array<{ x: number; y: number; originalIndex: number }>;
}

export interface PointWithData {
    entity?: Entity;
    query?: Query;
    isSearchResult?: boolean;
    originalIndex: number;
}

/**
 * Extract dimension values from entities and queries into a matrix format
 * Optionally applies weight scaling to each dimension before PCA computation
 */
function extractDimensionMatrix(
    entities: Entity[],
    queries: Query[],
    dimensions: Dimension[],
    pointsWithData: PointWithData[],
    dimensionWeights?: Record<string, number>,
    distanceMetric: "cosine" | "euclidean" | "manhattan" = "cosine",
    useNormalized: boolean = false
): number[][] {
    const matrix: number[][] = [];
    
    for (const point of pointsWithData) {
        const row: number[] = [];
        
        for (const dimension of dimensions) {
            let value: number | undefined;
            
            if (point.entity) {
                const metadata = point.entity.embedding?.metadata;
                const preferredKey = useNormalized ? 'normalized_entity_dimension_index' : 'entity_dimension_index';
                const dimensionIndex = metadata?.[preferredKey] ?? metadata?.['entity_dimension_index'];
                if (dimensionIndex) {
                    value = dimensionIndex[dimension.name] !== undefined
                        ? dimensionIndex[dimension.name]
                        : dimensionIndex[dimension.id];
                }
            } else if (point.query) {
                const preferred = useNormalized ? point.query.normalized_oasis_embedding : point.query.oasis_embedding;
                const oasisEmbedding = preferred ?? point.query.oasis_embedding;
                if (oasisEmbedding) {
                    const oasisVal = oasisEmbedding[dimension.name] !== undefined
                        ? oasisEmbedding[dimension.name]
                        : oasisEmbedding[dimension.id];
                    value = oasisVal ?? undefined;
                }
            }
            
            // Use 0 for missing values (PCA will handle this)
            row.push(value !== undefined ? value : 0);
        }
        
        matrix.push(row);
    }
    
    // Apply weight scaling if weights provided
    // Scaling depends on distance metric:
    // - Euclidean/Cosine: sqrt(weight)
    // - Manhattan: weight (linear)
    if (dimensionWeights && Object.keys(dimensionWeights).length > 0) {
        // Detect if weights are 0-1 or 0-100 scale
        const maxWeight = Math.max(...Object.values(dimensionWeights));
        const isNormalized = maxWeight <= 1;
        
        for (let col = 0; col < dimensions.length; col++) {
            const rawWeight = dimensionWeights[dimensions[col].name] ?? (isNormalized ? 1 : 100);
            const normalizedWeight = isNormalized ? rawWeight : rawWeight / 100;
            // Use linear scaling for Manhattan, sqrt for Euclidean/Cosine
            const scale = distanceMetric === "manhattan" ? normalizedWeight : Math.sqrt(normalizedWeight);
            for (let row = 0; row < matrix.length; row++) {
                matrix[row][col] *= scale;
            }
        }
    }
    
    return matrix;
}

/**
 * Fit a PCA model on the given points (determines eigenvectors, means, stds).
 * Does NOT project any points — use projectPointsThroughFit for that.
 */
export function fitPCA(
    entities: Entity[],
    queries: Query[],
    selectedDimensions: Dimension[],
    fittingPoints: PointWithData[],
    dimensionWeights?: Record<string, number>,
    distanceMetric: "cosine" | "euclidean" | "manhattan" = "cosine",
    useNormalized: boolean = false
): PCAFitResult | null {
    if (selectedDimensions.length < 2 || fittingPoints.length < 2) {
        return null;
    }

    const matrix = extractDimensionMatrix(entities, queries, selectedDimensions, fittingPoints, dimensionWeights, distanceMetric, useNormalized);
    const { standardized, means, stds } = standardizeMatrix(matrix);
    const pca = new PCA(standardized, { center: false, scale: false });

    const fitResult: PCAFitResult = {
        pcaModel: pca,
        standardizationParams: { means, stds },
        dimensionVectors: [],
        explainedVariance: { pc1: 0, pc2: 0, total: 0 },
    };

    // Dimension vectors: what happens if ONLY that dimension changes by 1 std
    const originPoint = [...means];
    const dimensionPoints: number[][] = [];
    for (let i = 0; i < selectedDimensions.length; i++) {
        const point = [...means];
        point[i] = means[i] + stds[i];
        dimensionPoints.push(point);
    }
    const transformedDimPoints = transformPointsThroughPCA([originPoint, ...dimensionPoints], fitResult);
    const originPCA = transformedDimPoints[0];

    fitResult.dimensionVectors = selectedDimensions.map((dimension, idx) => {
        const dimPointPCA = transformedDimPoints[idx + 1];
        const x = dimPointPCA[0] - originPCA[0];
        const y = dimPointPCA[1] - originPCA[1];
        return { dimension, x, y, magnitude: Math.sqrt(x * x + y * y) };
    });

    const ev = pca.getExplainedVariance();
    fitResult.explainedVariance = {
        pc1: ev[0] || 0,
        pc2: ev[1] || 0,
        total: ev.reduce((s: number, v: number) => s + v, 0),
    };

    return fitResult;
}

/**
 * Project a set of points through an already-fitted PCA model.
 * Returns the transformed 2D coordinates with original indices preserved.
 */
export function projectPointsThroughFit(
    entities: Entity[],
    queries: Query[],
    selectedDimensions: Dimension[],
    pointsWithData: PointWithData[],
    fit: PCAFitResult,
    dimensionWeights?: Record<string, number>,
    distanceMetric: "cosine" | "euclidean" | "manhattan" = "cosine",
    useNormalized: boolean = false
): Array<{ x: number; y: number; originalIndex: number }> {
    if (pointsWithData.length === 0) return [];

    const matrix = extractDimensionMatrix(entities, queries, selectedDimensions, pointsWithData, dimensionWeights, distanceMetric, useNormalized);
    const projected = transformPointsThroughPCA(matrix, fit);
    return projected.map((point, idx) => ({
        x: point[0],
        y: point[1],
        originalIndex: pointsWithData[idx].originalIndex,
    }));
}

/**
 * Convenience wrapper: fit PCA on the given points AND project them in one call.
 * Equivalent to fitPCA + projectPointsThroughFit on the same set.
 */
export function computePCA(
    entities: Entity[],
    queries: Query[],
    selectedDimensions: Dimension[],
    pointsWithData: PointWithData[],
    dimensionWeights?: Record<string, number>,
    distanceMetric: "cosine" | "euclidean" | "manhattan" = "cosine",
    useNormalized: boolean = false
): PCAResult | null {
    const fit = fitPCA(entities, queries, selectedDimensions, pointsWithData, dimensionWeights, distanceMetric, useNormalized);
    if (!fit) return null;

    const transformedPoints = projectPointsThroughFit(entities, queries, selectedDimensions, pointsWithData, fit, dimensionWeights, distanceMetric, useNormalized);

    return { ...fit, transformedPoints };
}

/**
 * Standardize a matrix (mean = 0, std = 1) column-wise
 * Returns both the standardized matrix and the standardization parameters
 */
function standardizeMatrix(matrix: number[][]): { standardized: number[][]; means: number[]; stds: number[] } {
    if (matrix.length === 0 || matrix[0].length === 0) {
        return { standardized: matrix, means: [], stds: [] };
    }
    
    const numRows = matrix.length;
    const numCols = matrix[0].length;
    
    // Calculate mean and std for each column
    const means: number[] = [];
    const stds: number[] = [];
    
    for (let col = 0; col < numCols; col++) {
        let sum = 0;
        for (let row = 0; row < numRows; row++) {
            sum += matrix[row][col];
        }
        const mean = sum / numRows;
        means.push(mean);
        
        let variance = 0;
        for (let row = 0; row < numRows; row++) {
            variance += Math.pow(matrix[row][col] - mean, 2);
        }
        const std = Math.sqrt(variance / numRows) || 1; // Avoid division by zero
        stds.push(std);
    }
    
    // Standardize
    const standardized: number[][] = [];
    for (let row = 0; row < numRows; row++) {
        const standardizedRow: number[] = [];
        for (let col = 0; col < numCols; col++) {
            standardizedRow.push((matrix[row][col] - means[col]) / stds[col]);
        }
        standardized.push(standardizedRow);
    }
    
    return { standardized, means, stds };
}

/**
 * Transform new points through an existing PCA model
 * @param points Points in original dimension space [point][dimension]
 * @param pcaResult The PCA result containing the model and standardization parameters
 * @returns Transformed points in PCA space [point][pc] where pc is [pc1, pc2]
 */
export function transformPointsThroughPCA(
    points: number[][],
    pcaResult: PCAFitResult
): number[][] {
    if (points.length === 0) {
        return [];
    }
    
    const { means, stds } = pcaResult.standardizationParams;
    const numDims = means.length;
    
    // Standardize the input points using stored parameters
    const standardized: number[][] = [];
    for (const point of points) {
        const standardizedPoint: number[] = [];
        for (let i = 0; i < numDims; i++) {
            const value = point[i] ?? 0;
            const mean = means[i] ?? 0;
            const std = stds[i] || 1;
            standardizedPoint.push((value - mean) / std);
        }
        standardized.push(standardizedPoint);
    }
    
    // Transform through PCA model
    const transformed = pcaResult.pcaModel.predict(standardized, { nComponents: 2 });
    
    // Convert to array if it's a Matrix object
    let transformedArray: number[][];
    if (Array.isArray(transformed) && Array.isArray(transformed[0])) {
        transformedArray = transformed as number[][];
    } else if (transformed && typeof (transformed as any).to2DArray === 'function') {
        transformedArray = (transformed as any).to2DArray();
    } else if (transformed && typeof (transformed as any).getRow === 'function') {
        const matrix = transformed as any;
        transformedArray = [];
        for (let i = 0; i < matrix.rows; i++) {
            transformedArray.push(matrix.getRow(i));
        }
    } else {
        // Fallback: try to access as 2D array
        try {
            transformedArray = transformed as any;
            if (!Array.isArray(transformedArray) || !Array.isArray(transformedArray[0])) {
                throw new Error('Not a valid array');
            }
        } catch {
            console.error('Could not convert transformed result to array:', transformed);
            throw new Error('PCA predict returned unexpected format');
        }
    }
    
    return transformedArray;
}
